import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { AdminNavigation } from "@/components/layout/Navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { toast } from "sonner";
import { 
  MessageSquare, 
  DollarSign, 
  TrendingUp, 
  Users, 
  ArrowUpDown,
  AlertTriangle,
  CheckCircle,
  Sparkles,
  CreditCard,
  RefreshCw,
  CalendarDays,
  Play
} from "lucide-react";

export default function AdminPathfinder() {
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);
  const [sortField, setSortField] = useState("messages_used");
  const [sortDirection, setSortDirection] = useState("desc");
  const [reflectionStats, setReflectionStats] = useState(null);
  const [triggeringReflections, setTriggeringReflections] = useState(false);

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/pathfinder/usage`, {
        withCredentials: true
      });
      setUsage(res.data);
    } catch (error) {
      toast.error("Failed to load PathFinder usage");
      console.error("Usage fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReflectionStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/weekly-reflections/stats`, {
        withCredentials: true
      });
      setReflectionStats(res.data);
    } catch (error) {
      console.error("Reflection stats error:", error);
    }
  }, []);

  const triggerReflections = async () => {
    setTriggeringReflections(true);
    try {
      await axios.post(`${API}/admin/weekly-reflections/trigger`, {}, {
        withCredentials: true
      });
      toast.success("Weekly reflections job triggered");
      await fetchReflectionStats();
    } catch (error) {
      toast.error("Failed to trigger reflections");
      console.error("Trigger error:", error);
    } finally {
      setTriggeringReflections(false);
    }
  };

  useEffect(() => {
    fetchUsage();
    fetchReflectionStats();
  }, [fetchUsage, fetchReflectionStats]);

  const sortUsers = (users) => {
    if (!users) return [];
    return [...users].sort((a, b) => {
      let aVal = a[sortField] || 0;
      let bVal = b[sortField] || 0;
      
      if (sortDirection === "desc") {
        return bVal - aVal;
      }
      return aVal - bVal;
    });
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "desc" ? "asc" : "desc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const SortButton = ({ field, children }) => (
    <button
      onClick={() => toggleSort(field)}
      className={`flex items-center gap-1 text-xs font-medium hover:text-[#a69373] transition-colors ${
        sortField === field ? "text-[#a69373]" : "text-white/60"
      }`}
    >
      {children}
      <ArrowUpDown className="w-3 h-3" />
    </button>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <main className="pt-24 px-4 max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-muted rounded-lg w-1/3" />
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-24 bg-muted rounded-xl" />
              ))}
            </div>
            <div className="h-64 bg-muted rounded-xl" />
          </div>
        </main>
      </div>
    );
  }

  const ceilingPercentage = usage?.totals 
    ? Math.min(100, (usage.totals.total_cost_usd / usage.totals.ceiling_usd) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <section className="mb-8 animate-fade-in flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: '#a69373' }}>
              <Sparkles className="w-8 h-8 inline mr-2" />
              PathFinder Usage
            </h1>
            <p className="text-[#a69373]/70">
              Monitor PathFinder usage and costs for {usage?.month || "current month"}
            </p>
          </div>
          <Button
            onClick={fetchUsage}
            variant="outline"
            className="border-[#a69373]/30 text-[#a69373] hover:bg-[#a69373]/10"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </section>

        {/* Summary Cards */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <MessageSquare className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-white/60">Messages Used</p>
                  <p className="text-2xl font-bold text-white">{usage?.totals?.total_messages || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <DollarSign className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-white/60">Total Cost</p>
                  <p className="text-2xl font-bold text-white">${usage?.totals?.total_cost_usd?.toFixed(2) || "0.00"}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <CreditCard className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-white/60">Top-ups Sold</p>
                  <p className="text-2xl font-bold text-white">{usage?.topups?.total_topups || 0}</p>
                  {usage?.topups?.total_revenue_zar > 0 && (
                    <p className="text-xs text-purple-400">R{usage.topups.total_revenue_zar.toFixed(0)}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${usage?.totals?.ceiling_reached ? "bg-red-500/20" : "bg-amber-500/20"}`}>
                  {usage?.totals?.ceiling_reached ? (
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                  ) : (
                    <TrendingUp className="w-5 h-5 text-amber-400" />
                  )}
                </div>
                <div>
                  <p className="text-xs text-white/60">Ceiling Remaining</p>
                  <p className={`text-2xl font-bold ${usage?.totals?.ceiling_reached ? "text-red-400" : "text-white"}`}>
                    ${usage?.totals?.ceiling_remaining_usd?.toFixed(2) || "50.00"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Ceiling Progress Bar */}
        <section className="mb-8">
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white/70">Monthly Spend vs $50 Ceiling</span>
                <span className="text-sm font-medium text-[#a69373]">
                  ${usage?.totals?.total_cost_usd?.toFixed(2) || "0.00"} / ${usage?.totals?.ceiling_usd || 50}
                </span>
              </div>
              <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ${
                    ceilingPercentage >= 90 ? "bg-red-500" :
                    ceilingPercentage >= 70 ? "bg-amber-500" :
                    "bg-green-500"
                  }`}
                  style={{ width: `${ceilingPercentage}%` }}
                />
              </div>
              {usage?.totals?.ceiling_reached && (
                <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Ceiling reached! PathFinder is disabled until next month.
                </p>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Weekly Reflections Section */}
        <section className="mb-8">
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-[#a69373]" />
                  Weekly Reflections
                </h2>
                <Button
                  onClick={triggerReflections}
                  disabled={triggeringReflections}
                  data-testid="trigger-reflections-btn"
                  variant="outline"
                  size="sm"
                  className="border-[#a69373]/30 text-[#a69373] hover:bg-[#a69373]/10"
                >
                  {triggeringReflections ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Trigger Now
                    </>
                  )}
                </Button>
              </div>
              
              {reflectionStats ? (
                <div className="space-y-4" data-testid="reflection-stats">
                  {/* Reflection Ceiling */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-white/60">This Month</p>
                      <p className="text-xl font-bold text-white">{reflectionStats.reflections_this_month || 0}</p>
                      <p className="text-xs text-white/40">reflections</p>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-white/60">Spend</p>
                      <p className="text-xl font-bold text-white">${reflectionStats.ceiling_status?.current_spend_usd?.toFixed(2) || "0.00"}</p>
                      <p className="text-xs text-white/40">of ${reflectionStats.ceiling_status?.ceiling_usd || 20} ceiling</p>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-white/60">Remaining</p>
                      <p className={`text-xl font-bold ${reflectionStats.ceiling_status?.ceiling_reached ? "text-red-400" : "text-green-400"}`}>
                        ${reflectionStats.ceiling_status?.remaining_usd?.toFixed(2) || "20.00"}
                      </p>
                      <p className="text-xs text-white/40">budget left</p>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-white/60">Status</p>
                      <p className={`text-xl font-bold flex items-center gap-1 ${reflectionStats.ceiling_status?.ceiling_reached ? "text-red-400" : "text-green-400"}`}>
                        {reflectionStats.ceiling_status?.ceiling_reached ? (
                          <>
                            <AlertTriangle className="w-4 h-4" />
                            Paused
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            Active
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  {/* Recent Job Logs */}
                  {reflectionStats.recent_job_logs?.length > 0 && (
                    <div className="border-t border-white/10 pt-4 mt-4">
                      <h3 className="text-sm font-medium text-white/70 mb-2">Recent Job Runs</h3>
                      <div className="space-y-2">
                        {reflectionStats.recent_job_logs.slice(0, 3).map((log, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm p-2 rounded bg-white/5">
                            <span className="text-white/60">
                              {new Date(log.executed_at).toLocaleString()}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              log.status === "completed" ? "bg-green-500/20 text-green-400" :
                              log.status === "skipped" ? "bg-amber-500/20 text-amber-400" :
                              "bg-red-500/20 text-red-400"
                            }`}>
                              {log.status || "completed"}
                            </span>
                            {log.generated !== undefined && (
                              <span className="text-white/40">
                                {log.generated} generated, {log.skipped || 0} skipped
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-white/50 text-sm">Loading reflection stats...</p>
              )}
            </CardContent>
          </Card>
        </section>

        {/* User Usage Table */}
        <section>
          <Card className="glass-card overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-[#a69373]" />
                Usage by User ({usage?.users?.length || 0})
              </h2>
            </div>
            
            {usage?.users?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/5">
                      <th className="text-left p-3 text-xs font-medium text-white/60">User</th>
                      <th className="text-center p-3">
                        <SortButton field="messages_used">Messages</SortButton>
                      </th>
                      <th className="text-center p-3">
                        <SortButton field="allowance_remaining">Allowance Left</SortButton>
                      </th>
                      <th className="text-center p-3">
                        <SortButton field="topup_balance">Top-up Balance</SortButton>
                      </th>
                      <th className="text-center p-3">
                        <SortButton field="total_cost_usd">Cost</SortButton>
                      </th>
                      <th className="text-center p-3 text-xs font-medium text-white/60">Tier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortUsers(usage.users).map((user, idx) => (
                      <tr 
                        key={user.user_id} 
                        className={`border-b border-white/5 hover:bg-white/5 transition-colors ${
                          idx % 2 === 0 ? "bg-transparent" : "bg-white/[0.02]"
                        }`}
                      >
                        <td className="p-3">
                          <div>
                            <p className="text-sm font-medium text-white">{user.name || "Unknown"}</p>
                            <p className="text-xs text-white/50">{user.email}</p>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <span className={`text-sm font-medium ${
                            user.messages_used >= user.monthly_allowance ? "text-red-400" : "text-white"
                          }`}>
                            {user.messages_used}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span className={`text-sm ${
                            user.allowance_remaining === 0 ? "text-red-400" :
                            user.allowance_remaining <= 10 ? "text-amber-400" :
                            "text-green-400"
                          }`}>
                            {user.allowance_remaining}/{user.monthly_allowance}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span className={`text-sm ${user.topup_balance > 0 ? "text-blue-400" : "text-white/40"}`}>
                            {user.topup_balance}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="text-sm text-white/80">
                            ${user.total_cost_usd?.toFixed(4) || "0.00"}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            user.subscription_type === "annual" ? "bg-amber-500/20 text-amber-400" :
                            user.subscription_type === "quarterly" ? "bg-purple-500/20 text-purple-400" :
                            user.subscription_type === "monthly" ? "bg-blue-500/20 text-blue-400" :
                            "bg-white/10 text-white/50"
                          }`}>
                            {user.subscription_type || "free"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400/50" />
                <p className="text-white/60">No PathFinder usage this month yet</p>
              </div>
            )}
          </Card>
        </section>
      </main>
    </div>
  );
}
